import { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { PipelinesTableV2 } from '@/features/pipelines/components/PipelinesTableV2';
import { queryPipelines } from '@/features/pipelines/data';
import {
  pipelineDefaultSortDirection,
  pipelineDefaultSortKey,
  type PipelineSortDirection,
  pipelineSortFieldMap,
  type PipelineSortKey,
  type PipelineTableStatus,
} from '@/features/pipelines/pages/pipelinesTableDefinition';
import { type PipelineStatusKey } from '@/features/quality/components/qualityStatusDataHelpers';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { useDateWindow, type DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildContainsFilter,
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import {
  applyFilteredRunSummariesToPipelines,
  fetchFilteredPipelineRunSummaries,
  groupPipelineRunSummariesByPipelineId,
} from '../hooks/pipelineFilteredRows';
import { buildPipelineEntityFilters } from '../hooks/pipelinePageFilters';
import type { TagOption } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { BuildStatus, PipelineListItemDto, QueryResult } from '@flowqa/shared';

export type PipelinesTablePanelProps = {
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  projectFilterProjects: string[];
  onProjectFilterProjectsChange: (next: string[]) => void;
  projectFilterOptions?: Array<{ value: string; label: string }>;
  deltasEnabled: boolean;
  pipelineSearchValue: string;
  onPipelineSearchChange: (value: string) => void;
  pipelineFilterStatuses: PipelineTableStatus[];
  onPipelineFilterStatusesChange: (next: PipelineTableStatus[]) => void;
  pipelineFilterPipelines: string[];
  onPipelineFilterPipelinesChange: (next: string[]) => void;
  pipelineFilterTags: string[];
  onPipelineFilterTagsChange: (next: string[]) => void;
  pipelineDateQuickFilter: DateQuickFilter;
  onPipelineDateQuickFilterChange: (next: DateQuickFilter) => void;
  pipelineFromDate: Dayjs | null;
  pipelineToDate: Dayjs | null;
  latestQueryStartDay: string | null;
  latestQueryEndDay: string | null;
  onPipelineFromDateChange: (next: Dayjs | null) => void;
  onPipelineToDateChange: (next: Dayjs | null) => void;
  tagOptions: TagOption[];
  windowedStatusPipelineIds: string[] | null;
};

const toLatestBuildStatusFilterValues = (
  statuses: PipelineStatusKey[],
): Array<BuildStatus | null> => {
  const mapped = statuses.flatMap((status): Array<BuildStatus | null> => {
    if (status === 'none') return [null];
    if (status === 'success') return ['completed'];
    if (status === 'running') return ['running'];
    if (status === 'queued') return ['queued'];
    return ['failed'];
  });
  return Array.from(new Set(mapped));
};

export const PipelinesTablePanel = ({
  selectedProjectId,
  selectedOrganisationId,
  deltasEnabled,
  pipelineSearchValue,
  pipelineFilterStatuses,
  pipelineFilterPipelines,
  pipelineFilterTags,
  pipelineDateQuickFilter,
  pipelineFromDate,
  pipelineToDate,
  latestQueryStartDay,
  latestQueryEndDay,
  tagOptions,
  windowedStatusPipelineIds,
}: PipelinesTablePanelProps): ReactElement => {
  const [pipelineSortKey, setPipelineSortKey] = useState<PipelineSortKey>(pipelineDefaultSortKey);
  const [pipelineSortDirection, setPipelineSortDirection] = useState<PipelineSortDirection>(
    pipelineDefaultSortDirection,
  );
  const pipelineDateWindow = useDateWindow({
    dateQuickFilter: pipelineDateQuickFilter,
    fromDate: pipelineFromDate,
    toDate: pipelineToDate,
    latestQueryStartDay,
    latestQueryEndDay,
  });

  const pipelineBuildStatusFilters = useMemo<GridQueryInput['filters']>(() => {
    if (pipelineFilterStatuses.length === 0) return [];
    const values = toLatestBuildStatusFilterValues(pipelineFilterStatuses);
    return buildInFilterWithNull('latestRunStatus', values);
  }, [pipelineFilterStatuses]);

  const pipelineQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<PipelineListItemDto>> => {
      if (windowedStatusPipelineIds !== null && windowedStatusPipelineIds.length === 0) {
        return {
          items: [],
          total: 0,
          page: input.page,
          pageSize: input.pageSize,
        };
      }

      const toolbarFilters = mergeFilters(
        buildContainsFilter('name', pipelineSearchValue),
        windowedStatusPipelineIds === null
          ? pipelineBuildStatusFilters
          : buildPipelineEntityFilters(windowedStatusPipelineIds),
        buildPipelineEntityFilters(pipelineFilterPipelines),
        buildInFilter('tags', pipelineFilterTags),
      );
      const sortOverride: ApiSort = {
        field: pipelineSortFieldMap[pipelineSortKey],
        direction: pipelineSortDirection,
      };
      const result = await queryPipelines(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        sorts: [sortOverride],
        filters: [...input.filters, ...toolbarFilters],
        metricsStartDay: pipelineDateWindow.startDay ?? undefined,
        metricsEndDay: pipelineDateWindow.endDay ?? undefined,
        previousMetricsStartDay: pipelineDateWindow.previousStartDay ?? undefined,
        previousMetricsEndDay: pipelineDateWindow.previousEndDay ?? undefined,
        include: {
          latestRun: true,
          runSummaries: true,
          metrics: true,
          metricDeltas: deltasEnabled,
        },
      });

      if (windowedStatusPipelineIds === null || pipelineFilterStatuses.length === 0) {
        return result;
      }

      const filteredRunSummaries = await fetchFilteredPipelineRunSummaries({
        selectedProjectId,
        selectedOrganisationId,
        pipelineIds: result.items.map((pipeline) => pipeline.id),
        statuses: pipelineFilterStatuses,
        selectedTags: pipelineFilterTags,
        startDay: pipelineDateWindow.startDay,
        endDay: pipelineDateWindow.endDay,
      });

      return {
        ...result,
        items: applyFilteredRunSummariesToPipelines(
          result.items,
          groupPipelineRunSummariesByPipelineId(filteredRunSummaries),
        ),
      };
    },
    [
      deltasEnabled,
      pipelineSearchValue,
      pipelineDateWindow.endDay,
      pipelineDateWindow.previousEndDay,
      pipelineDateWindow.previousStartDay,
      pipelineDateWindow.startDay,
      pipelineBuildStatusFilters,
      pipelineFilterPipelines,
      pipelineFilterStatuses,
      pipelineFilterTags,
      pipelineSortDirection,
      pipelineSortKey,
      selectedOrganisationId,
      selectedProjectId,
      windowedStatusPipelineIds,
    ],
  );

  const pipelineNoRowsText = pipelineDateWindow.hasCustomBounds
    ? getEmptyStateMessage('entity.noneForSelectedRange', { entity: 'pipeline' })
    : formatRangeWindowNoDataText(
        getEmptyStateMessage('entity.noneFound', { entity: 'pipeline' }),
        pipelineDateQuickFilter,
        false,
      );
  const pipelineStatusFilterKey = useMemo(
    () => [...pipelineFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [pipelineFilterStatuses],
  );
  const pipelineTagFilterKey = useMemo(
    () => [...pipelineFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [pipelineFilterTags],
  );
  const windowedStatusPipelineIdsKey = useMemo(
    () =>
      windowedStatusPipelineIds === null
        ? 'latest-snapshot'
        : [...windowedStatusPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
    [windowedStatusPipelineIds],
  );

  return (
    <Stack gap="xl">
      <PipelinesTableV2
        queryKey={[
          ...queryKeys.dataGrid(gridPaths.pipelines),
          pipelineDateQuickFilter,
          selectedOrganisationId ?? null,
          selectedProjectId ?? null,
          deltasEnabled ? 'deltas-on' : 'deltas-off',
          pipelineSearchValue,
          pipelineSortKey,
          pipelineSortDirection,
          pipelineStatusFilterKey,
          pipelineTagFilterKey,
          pipelineDateWindow.startDay,
          pipelineDateWindow.endDay,
          windowedStatusPipelineIdsKey,
        ]}
        queryFn={pipelineQueryFn}
        pageSizeOptions={[10, 25, 50]}
        noRowsText={pipelineNoRowsText}
        metricsEndDay={pipelineDateWindow.endDay}
        metricsStartDay={pipelineDateWindow.startDay}
        search={pipelineSearchValue}
        showProjectColumn={selectedProjectId === null}
        tagOptionsOverride={tagOptions}
        sort={{ field: pipelineSortKey, direction: pipelineSortDirection }}
        onSortChange={(nextSort) => {
          setPipelineSortKey(nextSort.field as PipelineSortKey);
          setPipelineSortDirection(nextSort.direction);
        }}
      />
    </Stack>
  );
};
